import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface Product {
  /** Translation key under the `products` namespace in home.json */
  key: string
  name: string
  href: string
  bgColor: string
  icon: string
  iconPath: string
  disabled?: boolean
  external?: boolean
}

const getProducts = (t: any): Product[] => [
  {
    key: 'synstreamenterprise',
    name: 'Synstream Enterprise',
    href: 'https://dashboard.nonprod.rtsinv.com/',
    bgColor: 'bg-linear-to-br from-green-50 via-emerald-50 to-green-100',
    icon: 'SE',
    iconPath: '/icon.png',
    external: true
  },
  {
    key: 'pipelinestudio',
    name: 'Pipeline Studio',
    href: '/products/pipeline-studio',
    bgColor: 'bg-linear-to-br from-green-50 via-emerald-50 to-green-100',
    icon: 'PS',
    iconPath: '/PS.png'
  },
  {
    key: 'automonitor',
    name: 'Auto Monitor',
    href: 'https://demo.eginnovations.com/',
    bgColor: 'bg-linear-to-br from-green-50 via-emerald-50 to-green-100',
    icon: 'AM',
    iconPath: '/AM.png',
    external: true
  },
  {
    key: 'totalcompliance',
    name: 'Total Compliance One Integrated Workflow',
    href: 'https://athena3-demo-uat.cynopsis.co/athena',
    bgColor: 'bg-linear-to-br from-green-50 via-emerald-50 to-green-100',
    icon: 'TC',
    iconPath: '/TC.svg',
    external: true
  }
]

export default function ProductsOverviewCard() {
  const { t } = useTranslation('home')
  const products = getProducts(t)

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl p-8 border border-gray-200/50 mb-8 transition-all duration-300">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-text-dark">{t('products.title')}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 auto-rows-fr">
        {products.map((product) => (
          product.disabled ? (
            <div key={product.name} className="group block h-full cursor-not-allowed">
              <div className={`${product.bgColor} h-full flex flex-col rounded-xl p-6 border border-white/50 backdrop-blur-sm opacity-60 relative`}>
                {/* Coming Soon Badge */}
                <div className="absolute top-4 right-4 bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                  Coming Soon
                </div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4">
                    <img
                      src={product.iconPath}
                      alt={product.name}
                      className="w-12 h-12 rounded-xl opacity-50"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-500">{t(`products.${product.key}.name`)}</h3>
                    <p className="text-gray-400 text-sm">
                      Feature under development
                    </p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm mb-4 leading-relaxed">
                  This feature is currently under development and will be available soon.
                </p>
                <div className="flex flex-wrap gap-2 mt-auto">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
                    Coming Soon
                  </span>
                </div>
              </div>
            </div>
          ) : product.external ? (
            <a
              key={product.name}
              href={product.href}
              className="group block h-full"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className={`${product.bgColor} h-full flex flex-col rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:scale-[1.01]`}>
                <div className="flex items-center min-h-14 mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4">
                    <img
                      src={product.iconPath}
                      alt={product.name}
                      className="w-12 h-12 rounded-xl"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{t(`products.${product.key}.name`)}</h3>
                    <p className="text-gray-600 text-sm">
                      {t(`products.${product.key}.subtitle`)}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                  {t(`products.${product.key}.description`)}
                </p>
                <div className="flex flex-wrap gap-2 mt-auto">
                  {(t(`products.${product.key}.features`, { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <span key={index} className={`px-3 py-1 rounded-full text-xs font-medium ${
                      product.name === 'Pipeline Studio'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </a>
          ) : (
            <Link
              key={product.name}
              to={product.href}
              className="group block h-full"
            >
              <div className={`${product.bgColor} h-full flex flex-col rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:scale-[1.01]`}>
                <div className="flex items-center min-h-14 mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4">
                    <img
                      src={product.iconPath}
                      alt={product.name}
                      className="w-12 h-12 rounded-xl"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{t(`products.${product.key}.name`)}</h3>
                    <p className="text-gray-600 text-sm">
                      {t(`products.${product.key}.subtitle`)}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                  {t(`products.${product.key}.description`)}
                </p>
                <div className="flex flex-wrap gap-2 mt-auto">
                  {(t(`products.${product.key}.features`, { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <span key={index} className={`px-3 py-1 rounded-full text-xs font-medium ${
                      product.name === 'Pipeline Studio'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          )
        ))}
      </div>
    </div>
  )
}